import { useScopedQuery } from '@/lib/scopedQuery';
import { api } from './client';

/*
 * Dashboard client-trends gadget source — GET /api/dashboard/client-trends.
 *
 * The server aggregates per-target averages over the selected window vs the
 * window before it and returns only names, averages, and point counts —
 * raw data points never cross the wire for the dashboard (HIPAA
 * minimum-necessary; the per-target deep dive lives in analytics).
 * Scope is always the caller's *assigned* clients, even for view-all roles.
 */

export type TrendWindow = 7 | 14 | 30;

export type TrendDataType = 'trial' | 'percent' | 'frequency' | 'duration' | 'task_analysis';

export interface TrendWindowStats {
    /** AVG(value_numeric) over the window, or null when the window has no points. */
    avg: number | null;
    /** Number of data points in the window. */
    n: number;
}

export interface TrendTarget {
    id: string;
    name: string;
    dataType: TrendDataType;
    /** Which way "better" points for this target — drives improving/declining. */
    masteryDirection: 'at_or_above' | 'at_or_below';
    current: TrendWindowStats;
    previous: TrendWindowStats;
}

export interface TrendProgram {
    id: string;
    name: string;
    targets: TrendTarget[];
}

export interface TrendClient {
    id: string;
    name: string;
    programs: TrendProgram[];
}

export interface ClientTrendsResponse {
    windowDays: TrendWindow;
    clients: TrendClient[];
}

async function fetchClientTrends(windowDays: TrendWindow): Promise<ClientTrendsResponse> {
    const res = await api.get<ClientTrendsResponse>('/dashboard/client-trends', {
        params: { windowDays },
    });
    return res.data;
}

export function useClientTrends(windowDays: TrendWindow, opts?: { enabled?: boolean }) {
    return useScopedQuery({
        queryKey: ['dashboard', 'client-trends', windowDays] as const,
        queryFn: () => fetchClientTrends(windowDays),
        enabled: opts?.enabled ?? true,
        // Keep the previous window's rows on screen while the new window
        // loads so toggling 1w/2w/1m doesn't collapse the gadget.
        placeholderData: (prev) => prev,
        staleTime: 5 * 60_000,
    });
}
